import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  constants,
  isHidden,
  isSuspended,
  SiteConfigManager,
  useApplicationState,
  useEventState,
  usePreferences,
} from '@atlas/core';
import { EventLink } from '@atlas/routing';
import { EventSimple, FavoriteButton, SimpleEvent as SimpleEventWithFavorite } from '@atlas/ui';

import { SPORT_IDS_WITH_DETAILED_SCORE } from 'common/constants';

import SimpleEventMarketTypes from './SimpleEventMarketTypes';
import TimeStatus from './TimeStatus';

const emptyMainMarketTypesByPeriod = [[{}]];

const SimpleEvent = props => {
  const { mainMarketTypesByPeriod, suspended, withoutFavoriteBtn } = props;
  const { getIsEventFavorite, selectFavoriteEvent } = usePreferences();
  const { authenticated } = useApplicationState();

  const event = useEventState();
  const {
    id,
    info: {
      score: { awayScore, extraData, homeScore, liveStatusId },
      stats,
    },
    name,
    outright,
    participants,
    sportId,
    sportService,
  } = event;
  const isEventSuspended = suspended || isSuspended(event);
  const withFavorite = SiteConfigManager.getConfig(
    'config.config.sportsbook.enableFavouritesInSimplified',
  );

  const mainMarketTypes = mainMarketTypesByPeriod?.length
    ? mainMarketTypesByPeriod
    : emptyMainMarketTypesByPeriod;

  const isLive = sportService === constants.SPORT_SERVICE.LIVE;

  const eventParticipants = React.useMemo(
    () =>
      outright
        ? undefined
        : {
            away: participants?.away?.fullName,
            home: participants?.home?.fullName,
          },
    [outright, participants?.away?.fullName, participants?.home?.fullName],
  );

  const isFavorite = getIsEventFavorite({ eventId: id });

  const setFavoriteEventHandler = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      selectFavoriteEvent({ eventId: id });
    },
    [selectFavoriteEvent, id],
  );

  const eventScore = React.useMemo(
    () =>
      isLive
        ? {
            away: awayScore,
            extraData,
            home: homeScore,
            liveStatusId,
          }
        : null,
    [isLive, awayScore, extraData, homeScore, liveStatusId],
  );

  const isEventHidden =
    isHidden(event) || (isEventSuspended && sportService === constants.SPORT_SERVICE.ARCHIVED);

  const detailedScore = React.useMemo(
    () => isLive && SPORT_IDS_WITH_DETAILED_SCORE.includes(sportId),
    [sportId, isLive],
  );

  if (isEventHidden) {
    return null;
  }

  if (withFavorite) {
    return (
      <EventLink className="event-simple-link" eventId={event.id} key={event.id}>
        <SimpleEventWithFavorite
          detailedScore={detailedScore}
          favoriteButton={
            authenticated &&
            !withoutFavoriteBtn && (
              <FavoriteButton active={isFavorite} onClick={setFavoriteEventHandler} />
            )
          }
          name={name}
          participants={eventParticipants}
          score={eventScore}
          sportId={sportId}
          stats={stats}
          timeStatus={<TimeStatus event={event} withFavorites />}
        >
          <SimpleEventMarketTypes
            event={event}
            mainMarketTypes={mainMarketTypes}
            suspended={isEventSuspended}
          />
        </SimpleEventWithFavorite>
      </EventLink>
    );
  }

  return (
    <EventLink className="event-simple-link" eventId={event.id} key={event.id}>
      <EventSimple
        detailedScore={detailedScore}
        name={name}
        participants={eventParticipants}
        score={eventScore}
        sportId={sportId}
        stats={stats}
        timeStatus={<TimeStatus event={event} />}
      >
        {!outright && (
          <SimpleEventMarketTypes
            event={event}
            mainMarketTypes={mainMarketTypes}
            suspended={isEventSuspended}
          />
        )}
      </EventSimple>
    </EventLink>
  );
};

SimpleEvent.propTypes = {
  mainMarketTypesByPeriod: PropTypes.array.isRequired,
  suspended: PropTypes.bool,
};

export default SimpleEvent;
